import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { PROPERTY_TAXES_ESCROW, PROPERTY_TAXES_NON_ESCROW } from '@ld.consumer-portal/constants';
import { PropertyTaxTopic, SecureMessageCategory } from '@ld.consumer-portal/models';
import { PlatformService } from '@ld.consumer-portal/utils';
import { ContactCardComponent } from '../contact-card/contact-card.component';
// eslint-disable-next-line @nx/enforce-module-boundaries -- compose message is a shared feature component used in @defer block
import { ComposeMessageComponent } from '@ld.consumer-portal/documents';

interface PropertyTaxFaq {
  key: string;
  heading: string;
  description: string;
  showUploadButton: boolean;
  uploadButtonText?: string;
  contactDetails?: string;
  address?: string;
  phone?: string;
  phoneDisplay?: string;
  fax?: string;
  faxDisplay?: string;
}

@Component({
  selector: 'ld-property-taxes-page',
  imports: [ComposeMessageComponent, ContactCardComponent, MatExpansionModule, MatIconModule, TranslocoPipe],
  templateUrl: './property-taxes-page.component.html',
  styleUrl: './property-taxes-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyTaxesPageComponent {
  private readonly platformService = inject(PlatformService);

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  protected readonly showComposeMessage = signal(false);
  protected readonly composePrefillCategory = SecureMessageCategory.PropertyTaxes;
  protected readonly composePrefillTopic = signal<string>(PropertyTaxTopic.PropertyTaxBill);

  protected readonly expandedPanel = signal<string | null>(null);

  protected readonly faqItems: PropertyTaxFaq[] = [
    {
      key: 'PropertyTaxBill',
      heading: 'propertyTaxesFaqBillHeading',
      description: 'propertyTaxesFaqBillDescription',
      showUploadButton: true,
      uploadButtonText: 'propertyTaxesUploadBillButton',
      contactDetails: 'propertyTaxesFaqContactDetails',
      address: PROPERTY_TAXES_ESCROW.address,
      phone: PROPERTY_TAXES_ESCROW.phone,
      phoneDisplay: PROPERTY_TAXES_ESCROW.phoneDisplay,
      fax: PROPERTY_TAXES_ESCROW.fax,
      faxDisplay: PROPERTY_TAXES_ESCROW.faxDisplay,
    },
    {
      key: 'DelinquentTaxNotice',
      heading: 'propertyTaxesFaqDelinquentHeading',
      description: 'propertyTaxesFaqDelinquentDescription',
      showUploadButton: true,
      uploadButtonText: 'propertyTaxesUploadProofButton',
      contactDetails: 'propertyTaxesFaqContactDetails',
      address: PROPERTY_TAXES_NON_ESCROW.address,
      phone: PROPERTY_TAXES_NON_ESCROW.phone,
      phoneDisplay: PROPERTY_TAXES_NON_ESCROW.phoneDisplay,
      fax: PROPERTY_TAXES_NON_ESCROW.fax,
      faxDisplay: PROPERTY_TAXES_NON_ESCROW.faxDisplay,
    },
    {
      key: 'SupplementalTaxBill',
      heading: 'propertyTaxesFaqSupplementalHeading',
      description: 'propertyTaxesFaqSupplementalDescription',
      showUploadButton: false,
    },
    {
      key: 'TaxAssessmentNotice',
      heading: 'propertyTaxesFaqAssessmentHeading',
      description: 'propertyTaxesFaqAssessmentDescription',
      showUploadButton: false,
    },
  ];

  togglePanel(key: string): void {
    this.expandedPanel.update((current) => (current === key ? null : key));
  }

  openSecureMessage(topic: string): void {
    this.composePrefillTopic.set(topic);
    this.showComposeMessage.set(true);
  }

  onCloseComposeMessage(): void {
    this.showComposeMessage.set(false);
  }
}
